// Angular Files
import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

// Other External Files
import { BehaviorSubject, Observable } from 'rxjs';

// Payment Integration Files
import { ConvenienceFeeConfigModel, PaymentMethodTypeEnum, } from 'apps/public-portal/src/app/payment-integrations/base/models';

// Teller Online Files
import {
    PaymentConfigDto
} from 'apps/public-portal/src/app/core/api/PaymentIntegrationApiClients';

export class PaymentProcessorConfig {
    /** Supported card brand values that have corresponding CSS styles */
    private static readonly SUPPORTED_CARD_BRANDS = ['visa', 'mastercard', 'amex', 'discover'];

    public convenienceFee: ConvenienceFeeConfigModel = new ConvenienceFeeConfigModel();

    /** Whether or not to display credit as a valid payment option */
    public isCreditEnabled: boolean = true;

    /** Whether or not to display e-check as a valid payment option */
    public isECheckEnabled: boolean = true;

    /** Whether or not to display Google Pay as a valid payment option */
    public isGooglePayEnabled: boolean = false;

    /** Whether or not to display Apple Pay as a valid payment option */
    public isApplePayEnabled: boolean = false;

    /** Convenience fee override used when paying by credit card */
    public creditConvenienceFee: ConvenienceFeeConfigModel = null;

    /** Convenience fee overrides used when paying by echeck */
    public eCheckConvenienceFee: ConvenienceFeeConfigModel = null;

    /** Indicative of which convenience fee type is currently being displayed to the user */
    public usingConvenienceFeeType: PaymentMethodTypeEnum;

    /** Whether or not to display validation fields for account and routing number on Echeck payments */
    public requireDualEntryForECheck: boolean = false;

    /** Whether to use VGS Collect iframe for echeck fields instead of the hardcoded echeck form */
    public useVgsForECheck: boolean = false;

    /** Contains any credit processor specific configuration. */
    public creditAdditionalData: { [key: string]: any };

    /** Contains any E-Check processor specific configuration */
    public eCheckAdditionalData: { [key: string]: any };

    /** Contains any credit processor specific test triggers. */
    public creditTestTriggerData: { [key: string]: string };

    /** Contains any E-Check processor specific test triggers */
    public eCheckTestTriggerData: { [key: string]: string };

    /** Card brands to display for credit card payment options (empty by default) */
    public acceptedCardBrands: string[] = [];

    /** Matches a single character the credit processor permits in a billing name */
    public creditAllowedBillingNameCharacterPattern: string = null;

    /** Matches a single character the e-check processor permits in a billing name */
    public eCheckAllowedBillingNameCharacterPattern: string = null;

    /** Longest billing name the credit processor accepts (null = no limit) */
    public creditMaxBillingNameLength: number = null;

    /** Longest billing name the e-check processor accepts (null = no limit) */
    public eCheckMaxBillingNameLength: number = null;

    private _defaultExpiryTime: number;

    private _creditExpiryTime: number;

    private _echeckExpiryTime: number;

    private _defaultCartHashRefreshTime: number;

    private _creditCartHashRefreshTime: number;

    private _echeckCartHashRefreshTime: number;

    /** Default convenience fee to use if either credit or echeck is missing */
    private _defaultConvenienceFee: ConvenienceFeeConfigModel = null;

    /** If the configuration was loaded correctly with valid configuration. */
    private _configLoaded: boolean = false;

    protected _convenienceFeeChanged: BehaviorSubject<ConvenienceFeeConfigModel> = new BehaviorSubject(null);

    public convenienceFeeChanged$: Observable<ConvenienceFeeConfigModel> = this._convenienceFeeChanged.asObservable();

    public get convenienceFeeType() {
        return this.usingConvenienceFeeType;
    }

    /** Switch the convenience fee based on the current payment method selected
     * If there is no config to switch to, will use the default config
     */
    public useConvenienceFee(paymentMethodType: PaymentMethodTypeEnum) {
        this.usingConvenienceFeeType = paymentMethodType;

        if (this._configLoaded) {
            // Set the fees to defaults to start, to be overridden later
            this.convenienceFee.enabled = this._defaultConvenienceFee.enabled;
            this.convenienceFee.format = this._defaultConvenienceFee.format;
            this.convenienceFee.rate = this._defaultConvenienceFee.rate;
            this.convenienceFee.note = this._defaultConvenienceFee.note;
            this.convenienceFee.label = this._defaultConvenienceFee.label;

            // A prepare-quoted fee belongs to the method it was quoted for. Same reset as
            // clearConvenienceFeeOverride, minus its emit: the emit at the end covers both
            this.convenienceFee.aggregateOverride = null;

            let feeOverride: ConvenienceFeeConfigModel = null;

            switch (paymentMethodType) {
                case PaymentMethodTypeEnum.CreditCard:
                    feeOverride = this.creditConvenienceFee;
                    break;
                case PaymentMethodTypeEnum.ECheck:
                    feeOverride = this.eCheckConvenienceFee;
                    break;
                default:
                    break;
            }

            if (feeOverride) {
                this.convenienceFee.enabled = feeOverride.enabled;
                // override the note as long as it's not undefined
                //  it still show if enabled = false so need a way to explicitly say "no note" which can be setting it to null
                if (typeof (feeOverride.note) !== undefined) this.convenienceFee.note = feeOverride.note;
                // only override these values if they are truthy
                if (feeOverride.format) this.convenienceFee.format = feeOverride.format;
                if (feeOverride.rate) this.convenienceFee.rate = feeOverride.rate;
                if (feeOverride.label) this.convenienceFee.label = feeOverride.label;
            }

            this._convenienceFeeChanged.next(this.convenienceFee);
        }
    }

    /**
     * Sets a cart-level aggregate convenience-fee override (the sum across
     * every partition), as returned by prepare-and-assess endpoints.
     * Stashed on <see cref="ConvenienceFeeConfigModel.aggregateOverride"/>
     * so <c>cartService.calculateTotals</c> can route it through the
     * cart-level <c>aggregateConvenienceFee</c> / <c>aggregateTotal</c>
     * getters, bypassing the per-partition fee pipeline that would
     * otherwise multiply the value by the partition count.
     */
    public overrideConvenienceFee(feeAmount: number) {
        this.convenienceFee.enabled = true;
        this.convenienceFee.aggregateOverride = feeAmount;

        this._convenienceFeeChanged.next(this.convenienceFee);
    }

    /**
     * Clears the cart-level aggregate override set by
     * {@link overrideConvenienceFee}. Called when navigating into a context
     * (e.g. the cart page) where per-partition fee math is the right source
     * of truth rather than the prepare-endpoint aggregate.
     */
    public clearConvenienceFeeOverride() {
        if (this.convenienceFee.aggregateOverride == null) return;
        this.convenienceFee.aggregateOverride = null;
        this._convenienceFeeChanged.next(this.convenienceFee);
    }

    public determineProcessorExpiryTime(paymentMethodType: PaymentMethodTypeEnum): number {
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                return this._creditExpiryTime;
            case PaymentMethodTypeEnum.ECheck:
                return this._echeckExpiryTime;
            default:
                return this._defaultExpiryTime;
        }
    }

    public determineCartHashRefreshTime(paymentMethodType: PaymentMethodTypeEnum): number {
        switch (paymentMethodType) {
            case PaymentMethodTypeEnum.CreditCard:
                return this._creditCartHashRefreshTime;
            case PaymentMethodTypeEnum.ECheck:
                return this._echeckCartHashRefreshTime;
            default:
                return this._defaultCartHashRefreshTime;
        }
    }

    /**
     * Rejects characters the processor will not accept in a billing name, reporting the
     * offending ones so the message can name them.
     *
     * An allow-list: a deny-list could only enumerate the characters someone thought to
     * list, and anything missed would pass here and fail at the processor instead.
     *
     * The pattern is read when the control is validated rather than when the validator is
     * built, because payment forms create their controls before {@link setupConfig} runs.
     */
    public billingNameValidator(paymentMethodType: PaymentMethodTypeEnum): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const pattern = paymentMethodType === PaymentMethodTypeEnum.CreditCard
                ? this.creditAllowedBillingNameCharacterPattern
                : this.eCheckAllowedBillingNameCharacterPattern;

            if (!pattern || !control.value) return null;

            const allowed = new RegExp(pattern, 'u');
            // Spread rather than split so astral characters count as one, and dedupe so a
            // name with two bad characters doesn't name the same one twice.
            const found = [...new Set([...control.value as string])]
                .filter(character => !allowed.test(character));

            return found.length ? { disallowedCharacters: { characters: found } } : null;
        };
    }

    /**
     * Rejects a billing name longer than the processor accepts.
     *
     * Deliberately not Validators.maxLength: that captures the limit when the validator is
     * built, and payment forms create their controls before {@link setupConfig} runs, so it
     * would capture null and never fire. Emits Angular's own error shape so the existing
     * "cannot exceed [#] characters" message applies unchanged.
     */
    public billingNameLengthValidator(paymentMethodType: PaymentMethodTypeEnum): ValidatorFn {
        return (control: AbstractControl): ValidationErrors | null => {
            const maxLength = paymentMethodType === PaymentMethodTypeEnum.CreditCard
                ? this.creditMaxBillingNameLength
                : this.eCheckMaxBillingNameLength;

            if (!maxLength || !control.value) return null;

            const actualLength = (control.value as string).length;
            return actualLength > maxLength
                ? { maxlength: { requiredLength: maxLength, actualLength } }
                : null;
        };
    }

    /** Every rule the processor imposes on a billing name, for one form control. */
    public billingNameValidators(paymentMethodType: PaymentMethodTypeEnum): ValidatorFn[] {
        return [
            this.billingNameValidator(paymentMethodType),
            this.billingNameLengthValidator(paymentMethodType)
        ];
    }

    // #region helpers

    /** Map the default config variables retrieved from the backend getConfig call */
    public setupConfig(dto: PaymentConfigDto) {
        // grab the default convenience config values
        this._defaultConvenienceFee = new ConvenienceFeeConfigModel();
        this._defaultConvenienceFee.fromDto(dto);

        this._defaultExpiryTime = dto.processorExpiryTime;
        this._defaultCartHashRefreshTime = dto.cartHashRefreshTime;

        // grab the credit config values
        if (dto.creditConfig) {
            this.isCreditEnabled = dto.creditConfig.enabled;
            this.creditConvenienceFee = new ConvenienceFeeConfigModel();
            this.creditConvenienceFee.fromDto(dto.creditConfig);
            this.creditAdditionalData = dto.creditConfig.additionalData;
            this.creditTestTriggerData = dto.creditConfig.testTriggers;
            this._creditExpiryTime = dto.creditConfig.processorExpiryTime;
            this.creditAllowedBillingNameCharacterPattern = dto.creditConfig.allowedBillingNameCharacterPattern;
            this.creditMaxBillingNameLength = dto.creditConfig.maxBillingNameLength;
            this._creditCartHashRefreshTime = dto.creditConfig.cartHashRefreshTime;

            // Map accepted card brands (only if explicitly configured)
            // Normalize to lowercase and filter to supported values since CSS classes expect lowercase brand names (e.g., "teller-online-icon--visa")
            if (dto.creditConfig.acceptedCardBrands?.length) {
                this.acceptedCardBrands = dto.creditConfig.acceptedCardBrands
                    .map(brand => brand.toLowerCase().trim())
                    .filter(brand => PaymentProcessorConfig.SUPPORTED_CARD_BRANDS.includes(brand));
            }
        }

        // grab the e-check config values
        if (dto.eCheckConfig) {
            this.isECheckEnabled = dto.eCheckConfig.enabled;
            this.eCheckConvenienceFee = new ConvenienceFeeConfigModel();
            this.eCheckConvenienceFee.fromDto(dto.eCheckConfig);
            this.eCheckAdditionalData = dto.eCheckConfig.additionalData;
            this.eCheckTestTriggerData = dto.eCheckConfig.testTriggers;
            this.requireDualEntryForECheck = dto.eCheckConfig.requireDualEntryForECheck;
            this.useVgsForECheck = dto.eCheckConfig.useVgsForECheck ?? false;
            this._echeckExpiryTime = dto.eCheckConfig.processorExpiryTime;
            this.eCheckAllowedBillingNameCharacterPattern = dto.eCheckConfig.allowedBillingNameCharacterPattern;
            this.eCheckMaxBillingNameLength = dto.eCheckConfig.maxBillingNameLength;
            this._echeckCartHashRefreshTime = dto.eCheckConfig.cartHashRefreshTime;
        }

        this.useConvenienceFee(this.usingConvenienceFeeType);
        this._configLoaded = true;
    }

    // #endregion
}
